import { formatCurrency, formatDate, formatDateTime } from '../lib/format.js';
import { WORK_ORDER_STATUS_LABELS } from '../../shared/workflow.js';
import type { WorkOrderStatus } from '../../shared/workflow.js';
import type { ApprovalReportRow, ReportRow } from '../api/types.js';

/**
 * The narrative form of the work order report: the same rows the CSV carries, laid
 * out as a document the Contracting Officer can read, file, or attach to an invoice.
 * The model is built once here and rendered three ways — on screen, into Word, and
 * through the browser's print dialog — so the three cannot disagree.
 *
 * Every value comes from the report endpoint, which is already scoped to the reader's
 * facilities and already reports the price USPS authorized rather than VGS cost, so a
 * customer reading this document sees exactly the customer-facing figures.
 */

export interface ReportField {
  label: string;
  value: string;
}

export interface ReportWorkOrder {
  number: string;
  title: string;
  fields: ReportField[];
}

export interface ReportLocation {
  key: string;
  heading: string;
  details: ReportField[];
  workOrders: ReportWorkOrder[];
}

export interface DetailedReport {
  title: string;
  scopeLine: string;
  periodLabel: string;
  generatedLabel: string;
  summary: ReportField[];
  overviewHeaders: string[];
  overviewRows: string[][];
  locations: ReportLocation[];
  footnote: string;
  isEmpty: boolean;
}

export interface DetailedReportOptions {
  from?: string;
  to?: string;
  generatedAt?: Date;
}

const NONE = '—';

const OVERVIEW_HEADERS = [
  'Work Order',
  'Commodity',
  'Status',
  'Received',
  'Approved (NTP)',
  'Project Cost',
  'Invoice',
];

const FOOTNOTE =
  'Project cost is the amount the Postal Service authorized. How the contractor arrived at ' +
  'that price is not part of this report.';

function textOf(row: ReportRow, key: string): string | null {
  const value = row[key];
  if (value === null || value === undefined || value === '') return null;
  return String(value);
}

function show(value: string | null): string {
  return value === null ? NONE : value;
}

function statusLabel(row: ReportRow): string {
  const status = textOf(row, 'status');
  if (status === null) return NONE;
  return WORK_ORDER_STATUS_LABELS[status as WorkOrderStatus] ?? status;
}

function priorityLabel(row: ReportRow): string {
  const priority = textOf(row, 'priority');
  if (priority === null) return NONE;
  return priority.charAt(0).toUpperCase() + priority.slice(1);
}

function costLabel(row: ReportRow): string {
  const cents = row.projectCostCents;
  return typeof cents === 'number' ? formatCurrency(cents) : NONE;
}

function dateLabel(row: ReportRow, key: string): string {
  const value = textOf(row, key);
  return value === null ? NONE : formatDate(value);
}

function instantLabel(row: ReportRow, key: string): string {
  const value = textOf(row, key);
  return value === null ? NONE : formatDateTime(value);
}

function numberLabel(row: ReportRow, key: string): string {
  const value = row[key];
  if (typeof value !== 'number') return NONE;
  // The value is still shown, since it is the evidence of the error, but a reader
  // comparing it against a summary that excludes it needs to know why.
  return value < 0 ? `${value.toFixed(1)} (dates run backwards)` : value.toFixed(1);
}

/**
 * The authority the decision was made under, taken from the approval record rather
 * than inferred from the amount, so a work order approved before a contract
 * modification still reports the ceiling that actually applied.
 */
function authorityByWorkOrder(approvals: readonly ApprovalReportRow[]): Map<string, string> {
  const found = new Map<string, string>();
  for (const approval of approvals) {
    if (approval.decision !== 'approved') continue;
    const label = approval.authorityBasis ?? approval.authorityLabel;
    if (label === null) continue;
    const existing = found.get(approval.workOrderNumber);
    if (existing === undefined) found.set(approval.workOrderNumber, label);
  }
  return found;
}

/**
 * A duration cannot run backwards. Where recorded dates make one do so the dates are
 * wrong, and averaging them in would present a negative cycle time as a measurement,
 * so they are excluded and the exclusion is stated rather than hidden.
 */
function average(values: number[]): string {
  const usable = values.filter((value) => value >= 0);
  if (usable.length === 0) return NONE;
  const mean = usable.reduce((sum, value) => sum + value, 0) / usable.length;
  const excluded = values.length - usable.length;
  const over = `over ${usable.length}`;
  return excluded === 0
    ? `${mean.toFixed(1)} (${over})`
    : `${mean.toFixed(1)} (${over}; ${excluded} excluded, dates run backwards)`;
}

function countWhere(rows: readonly ReportRow[], key: string): number {
  return rows.filter((row) => textOf(row, key) !== null).length;
}

function summarize(rows: readonly ReportRow[]): ReportField[] {
  const costs = rows
    .map((row) => row.projectCostCents)
    .filter((value): value is number => typeof value === 'number');
  const numbers = (key: string) =>
    rows
      .map((row) => row[key])
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

  const closed = new Set(['closed', 'cancelled', 'rejected']);

  return [
    { label: 'Work orders in report', value: String(rows.length) },
    { label: 'Approved', value: String(countWhere(rows, 'approvedAt')) },
    { label: 'Completed', value: String(countWhere(rows, 'completedAt')) },
    { label: 'Closed', value: String(countWhere(rows, 'closedAt')) },
    {
      label: 'Open at report date',
      value: String(rows.filter((row) => !closed.has(textOf(row, 'status') ?? '')).length),
    },
    {
      label: 'Total project cost',
      value: formatCurrency(costs.reduce((sum, value) => sum + value, 0)),
    },
    { label: 'Average days, request to approval', value: average(numbers('daysToApproval')) },
    {
      label: 'Average days, approval to completion',
      value: average(numbers('daysToCompletion')),
    },
  ];
}

function periodLabel(options: DetailedReportOptions): string {
  const from = options.from ? formatDate(options.from) : null;
  const to = options.to ? formatDate(options.to) : null;
  if (from && to) return `${from} – ${to}`;
  if (from) return `From ${from}`;
  if (to) return `Through ${to}`;
  return 'All dates';
}

function locationFields(row: ReportRow): ReportField[] {
  return [
    { label: 'Service Location Name', value: show(textOf(row, 'facilityName')) },
    { label: 'Service Location Code', value: show(textOf(row, 'facilityCode')) },
    { label: 'Service Location Address', value: show(textOf(row, 'serviceAddress')) },
    { label: 'Property ID', value: show(textOf(row, 'propertyId')) },
    { label: 'Postal Service Area / Logistics', value: show(textOf(row, 'uspsArea')) },
    { label: 'Postal Service District / Division', value: show(textOf(row, 'uspsDistrict')) },
    { label: 'Local Facility Contact', value: show(textOf(row, 'facilityContact')) },
    { label: 'Task Order', value: show(textOf(row, 'taskOrderNumber')) },
  ];
}

function workOrderFields(row: ReportRow, authority: string | undefined): ReportField[] {
  return [
    { label: 'Work Order Number', value: show(textOf(row, 'workOrderNumber')) },
    { label: 'Commodity or Service Provided', value: show(textOf(row, 'commodityOrService')) },
    { label: 'Work Description', value: show(textOf(row, 'workDescription')) },
    { label: 'Priority', value: priorityLabel(row) },
    { label: 'Project / Service Status', value: statusLabel(row) },
    { label: 'Requestor Contact', value: show(textOf(row, 'requestorContact')) },
    { label: 'Date Request Received', value: instantLabel(row, 'submittedAt') },
    { label: 'Date Request Approved (NTP)', value: instantLabel(row, 'approvedAt') },
    { label: 'Approval Authority', value: authority ?? NONE },
    { label: 'Project Cost', value: costLabel(row) },
    { label: 'Performance Start Date', value: dateLabel(row, 'performanceStartDate') },
    { label: 'Completed', value: instantLabel(row, 'completedAt') },
    { label: 'Closed', value: instantLabel(row, 'closedAt') },
    { label: 'Invoice Number', value: show(textOf(row, 'invoiceNumber')) },
    { label: 'Days, Request to Approval', value: numberLabel(row, 'daysToApproval') },
    { label: 'Days, Approval to Completion', value: numberLabel(row, 'daysToCompletion') },
    { label: 'Notes or Comments', value: show(textOf(row, 'notes')) },
  ];
}

function overviewRow(row: ReportRow): string[] {
  return [
    show(textOf(row, 'workOrderNumber')),
    show(textOf(row, 'commodityOrService')),
    statusLabel(row),
    dateLabel(row, 'submittedAt'),
    dateLabel(row, 'approvedAt'),
    costLabel(row),
    show(textOf(row, 'invoiceNumber')),
  ];
}

/** The distinct service locations in the rows, each keeping the rows it covers. */
function groupByLocation(
  rows: readonly ReportRow[],
  authority: Map<string, string>,
): ReportLocation[] {
  const locations = new Map<string, ReportLocation>();

  for (const row of rows) {
    const code = textOf(row, 'facilityCode');
    const name = textOf(row, 'facilityName');
    const key = code ?? name ?? 'unknown';
    let location = locations.get(key);
    if (location === undefined) {
      location = {
        key,
        heading: code === null ? (name ?? 'Service location') : `${name ?? key} (${code})`,
        details: locationFields(row),
        workOrders: [],
      };
      locations.set(key, location);
    }
    const number = show(textOf(row, 'workOrderNumber'));
    location.workOrders.push({
      number,
      title: show(textOf(row, 'title')),
      fields: workOrderFields(row, authority.get(number)),
    });
  }

  return [...locations.values()];
}

export function buildDetailedReport(
  rows: readonly ReportRow[],
  approvals: readonly ApprovalReportRow[],
  options: DetailedReportOptions = {},
): DetailedReport {
  const authority = authorityByWorkOrder(approvals);
  const locations = groupByLocation(rows, authority);
  const taskOrders = [
    ...new Set(rows.map((row) => textOf(row, 'taskOrderNumber')).filter((value) => value !== null)),
  ];

  const scopeLine =
    taskOrders.length === 0
      ? 'No task order in scope'
      : taskOrders.length === 1
        ? `Task order ${taskOrders[0]}`
        : `Task orders ${taskOrders.join(', ')}`;

  return {
    title: 'Detailed Work Order Report',
    scopeLine:
      locations.length <= 1
        ? scopeLine
        : `${scopeLine} · ${String(locations.length)} service locations`,
    periodLabel: periodLabel(options),
    generatedLabel: formatDateTime((options.generatedAt ?? new Date()).toISOString()),
    summary: summarize(rows),
    overviewHeaders: OVERVIEW_HEADERS,
    overviewRows: rows.map(overviewRow),
    locations,
    footnote: FOOTNOTE,
    isEmpty: rows.length === 0,
  };
}
